import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Star } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const FeedbackDialog = ({ open, onClose, sessionId }) => {
  const [rating, setRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [comment, setComment] = useState('');
  const [improvements, setImprovements] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (rating === 0) {
      toast.error('Bitte geben Sie eine Bewertung ab');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/feedback`, {
        session_id: sessionId,
        rating,
        comment,
        improvements
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });

      toast.success('Vielen Dank für Ihr Feedback!');
      
      // Mark as submitted (never ask again for this session)
      localStorage.setItem(`feedback_submitted_${sessionId}`, 'true');
      
      onClose();
      
      // Reset form
      setRating(0);
      setComment('');
      setImprovements('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern des Feedbacks');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSkip = () => {
    // Mark as skipped (never ask again for this session)
    localStorage.setItem(`feedback_skipped_${sessionId}`, 'true');
    onClose();
  };

  const handleDialogClose = (isOpen) => {
    if (!isOpen) {
      // Dialog wurde weggeklickt (X-Button) - später nochmal fragen
      localStorage.setItem(`feedback_dismissed_${sessionId}`, Date.now().toString());
    }
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleDialogClose}>
      <DialogContent 
        className="max-w-lg"
        style={{ backgroundColor: '#0F141A', borderColor: '#1F2937' }}
      >
        <DialogHeader>
          <DialogTitle style={{ color: '#E6EDF3' }}>
            Wie war Ihre Diagnose-Erfahrung?
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 mt-4">
          {/* Star Rating */}
          <div>
            <p className="text-sm mb-3" style={{ color: '#94A3B8' }}>
              Bewerten Sie die Qualität der Diagnose:
            </p>
            <div className="flex gap-2 justify-center">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  className="transition-transform hover:scale-110"
                  onMouseEnter={() => setHoveredRating(star)}
                  onMouseLeave={() => setHoveredRating(0)}
                  onClick={() => setRating(star)}
                >
                  <Star
                    className="w-10 h-10"
                    fill={(hoveredRating || rating) >= star ? '#F59E0B' : 'none'}
                    stroke={(hoveredRating || rating) >= star ? '#F59E0B' : '#94A3B8'}
                  />
                </button>
              ))}
            </div>
            {rating > 0 && (
              <p className="text-center mt-2 text-sm" style={{ color: '#F59E0B' }}>
                {rating === 5 && 'Ausgezeichnet! 🎉'}
                {rating === 4 && 'Sehr gut! 👍'}
                {rating === 3 && 'Gut 👌'}
                {rating === 2 && 'Okay 😐'}
                {rating === 1 && 'Verbesserungsbedarf 😞'}
              </p>
            )}
          </div>

          {/* Comment */}
          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: '#E6EDF3' }}>
              Ihr Kommentar (optional)
            </label>
            <Textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Was hat Ihnen gefallen oder nicht gefallen?"
              rows={3}
              style={{ backgroundColor: '#0B0F14', borderColor: '#1F2937', color: '#E6EDF3' }}
            />
          </div>

          {/* Improvements */}
          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: '#E6EDF3' }}>
              Verbesserungsvorschläge (optional)
            </label>
            <Textarea
              value={improvements}
              onChange={(e) => setImprovements(e.target.value)}
              placeholder="Was könnten wir besser machen? Welche Features wünschen Sie sich?"
              rows={3}
              style={{ backgroundColor: '#0B0F14', borderColor: '#1F2937', color: '#E6EDF3' }}
            />
          </div>

          {/* Buttons */}
          <div className="flex gap-3">
            <Button
              onClick={handleSkip}
              variant="outline"
              className="flex-1"
              disabled={submitting}
              style={{ borderColor: '#1F2937', color: '#94A3B8' }}
            >
              Überspringen
            </Button>
            <Button
              onClick={handleSubmit}
              className="flex-1"
              style={{ backgroundColor: '#1EA0E6', color: '#fff' }}
              disabled={submitting}
            >
              {submitting ? 'Wird gesendet...' : 'Feedback absenden'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
